import { create } from "zustand";

/**
 * Preços referenciais B3 por símbolo (evento SignalR "RP" — #437).
 *
 * Espelho do modelo legado: o Silverlight recebia a mensagem "RP:" de carona no
 * RegisterTrade e guardava num dicionário por símbolo; aqui o RP pega carona na
 * assinatura de quote e mora neste store isolado (padrão hoverInfoStore/newsStore
 * — nunca no chartStore nem no cache do quoteWorker).
 *
 * Consumidor primário: painel Info, linha "Ajuste" = `ps`
 * (PreviousDaySettlementPrice, settlement de D-1 — base do mark-to-market do
 * dia). REGRAS DE NEGÓCIO seladas no consenso da issue:
 * - exibir só quando `ps > 0`; ausente ⇒ a linha SOME. NUNCA cair para
 *   PC/fechamento: em futuro, fechamento ≠ ajuste — número plausível e errado é
 *   pior que nenhum (ver feedback_no_local_symbol_specs).
 * - lookup por chave EXATA: o Feeder já re-emite o RP keyed por alias (WINFUT/
 *   WINFUL) além do contrato — resolução de família aqui casaria contrato
 *   expirado no rollover.
 * - frescor é garantido no SERVIDOR (gate do snapshot por data do último trade);
 *   o cliente só mantém o guard de ordenação por `ts`.
 *
 * Volumetria: ~dezenas de RPs/dia/símbolo — handler fora do quoteWorker (I5);
 * escrita substitui o slice imutavelmente (I6).
 */
export const useReferentialPriceStore = create((set) => ({
  // { [symbol]: { ps, sp, ts, h, otx, otn, rtx, rtn, atx, atn } }
  bySymbol: {},

  /** Aplica um RP recebido (snapshot ou live). Ignora payload sem símbolo e
   *  descarta out-of-order (ts mais velho que o vigente). */
  applyReferentialPrice: (msg) => {
    if (!msg || !msg.S) return;
    const ts = msg.TS ? new Date(msg.TS).getTime() : 0;
    set((state) => {
      const current = state.bySymbol[msg.S];
      if (current && ts < current.ts) return state; // out-of-order: mantém o mais novo
      return {
        bySymbol: {
          ...state.bySymbol,
          [msg.S]: {
            ps: msg.PS,
            sp: msg.SP,
            ts,
            h: !!msg.H,
            otx: msg.OTx,
            otn: msg.OTn,
            rtx: msg.RTx,
            rtn: msg.RTn,
            atx: msg.ATx,
            atn: msg.ATn,
          },
        },
      };
    });
  },

  /** Limpa tudo (disconnect — mesmo padrão dos caches de continuidade, #201). */
  clear: () => set({ bySymbol: {} }),
}));

/** Ajuste (settlement D-1) do símbolo, ou null quando não exibível (ps ausente/0). */
export const getAjuste = (symbol) => {
  const entry = useReferentialPriceStore.getState().bySymbol[symbol];
  return entry && entry.ps > 0 ? entry.ps : null;
};
